import { RotateCcw, Send, Undo2 } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { IndexPage } from '../../../../components/index-page/IndexPage';
import type { ColumnDef, IndexQueryState } from '../../../../components/index-page/types';
import type { MenuItem } from '../../../../components/Menu';
import { StatusText } from '../../../../components/table/StatusText';
import { TextCell } from '../../../../components/table/TextCell';
import { ApiError } from '../../../../lib/apiError';
import { formatDateOnly } from '../../../../lib/formatDate';
import { formatMoney } from '../../../../lib/formatMoney';
import { progressPercent } from '../../../../components/progressPercent';
import { CancelPurchaseOrderModal } from '../components/CancelPurchaseOrderModal';
import { PurchaseOrderModal } from '../components/PurchaseOrderModal';
import { purchaseOrdersApi } from '../purchaseOrdersApi';
import { suppliersApi } from '../suppliersApi';
import { fulfilmentOf, PO_STATUS } from '../purchaseOrderTypes';
import type { PurchaseOrderListItem } from '../purchaseOrderTypes';

/**
 * The purchase-order register.
 *
 * **There is no Clear data**, and the absence is its own decision: emptying a register of what
 * the organization committed to buy destroys the accountability record — the call
 * `stock-movements`, `issuances` and `purchase-requests` already take.
 */
export function PurchaseOrdersIndexPage() {
  const navigate = useNavigate();

  const [creating, setCreating] = useState(false);
  const [cancelling, setCancelling] = useState<PurchaseOrderListItem | null>(null);
  const [confirming, setConfirming] = useState<{
    kind: 'delete' | 'restore' | 'submit';
    row: PurchaseOrderListItem;
  } | null>(null);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  /*
    THE PREREQUISITE, and it is the one thing this page has to know before the reader opens the
    form: an order names a supplier, so the registry has to hold a live row before one can be
    placed. `<IndexPage prerequisites>` says so above the table and disables Create with the same
    sentence — rather than letting the reader fill in a form and meet an empty required picker.

    SEEDED MET, and a failed feed LEAVES it met: flashing "set up suppliers first" on every load
    of a working module is worse than a banner arriving a moment late, and a request that did not
    come back is not evidence that a registry is empty.
  */
  const [hasSupplier, setHasSupplier] = useState(true);

  /*
    AND AN ORDERABLE REQUEST, which is the SECOND required picker on this form and was missed
    first time round. Step one is a required multi-select fed by `orderable-requests`: with
    nothing approved the reader opens the wizard, meets an empty list and a dead Next, and has
    nowhere to go — which is exactly the state the prerequisite banner exists to report BEFORE
    the form is opened rather than after.
  */
  const [hasOrderableRequest, setHasOrderableRequest] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    suppliersApi
      .options(controller.signal)
      .then((options) => {
        if (!controller.signal.aborted) setHasSupplier(options.length > 0);
      })
      .catch(() => {});

    purchaseOrdersApi
      .orderableRequests(controller.signal)
      .then((requests) => {
        if (!controller.signal.aborted) setHasOrderableRequest(requests.length > 0);
      })
      .catch(() => {});

    return () => controller.abort();
  }, [reloadKey]);

  const refresh = useCallback(() => setReloadKey((k) => k + 1), []);

  const fetchRows = useCallback(
    (query: IndexQueryState, signal: AbortSignal) =>
      purchaseOrdersApi.list(
        {
          search: query.search,
          page: query.page,
          pageSize: query.pageSize,
          sort: query.sort ?? undefined,
          status: query.facets.status?.join(','),
          stage: query.facets.stage?.join(','),
          supplier: query.facets.supplier?.join(','),
        },
        signal,
      ),
    [],
  );

  const rowActions = useCallback(
    (row: PurchaseOrderListItem): MenuItem[] => [
      {
        key: `submit-${row.id}`,
        label: 'Submit for approval',
        icon: <Send className="h-4 w-4" aria-hidden="true" />,
        permission: 'purchase-orders.submit',
        // Disabled for STATE with the reason — the reader holds the key, so a hidden entry
        // would say they lack it.
        disabledReason:
          row.status === PO_STATUS.draft ? null : 'Only a draft can be submitted for approval.',
        onClick: () => setConfirming({ kind: 'submit', row }),
      },
      {
        key: `cancel-${row.id}`,
        label: 'Cancel order',
        icon: <Undo2 className="h-4 w-4" aria-hidden="true" />,
        permission: 'purchase-orders.submit',
        disabledReason:
          row.status === PO_STATUS.cancelled ? 'This order has already been cancelled.' : null,
        onClick: () => setCancelling(row),
      },
      {
        key: `restore-${row.id}`,
        label: 'Restore',
        icon: <RotateCcw className="h-4 w-4" aria-hidden="true" />,
        permission: 'purchase-orders.delete',
        disabledReason: row.isActive ? 'This order is already active.' : null,
        onClick: () => setConfirming({ kind: 'restore', row }),
      },
    ],
    [],
  );

  async function runConfirmed() {
    if (!confirming) return;

    setRunning(true);
    setError(null);

    try {
      if (confirming.kind === 'delete') await purchaseOrdersApi.remove(confirming.row.id);
      else if (confirming.kind === 'restore') await purchaseOrdersApi.restore(confirming.row.id);
      else await purchaseOrdersApi.submit(confirming.row.id);

      setConfirming(null);
      refresh();
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught : new ApiError('The action could not be completed.', 0),
      );
    } finally {
      setRunning(false);
    }
  }

  return (
    <>
      <IndexPage<PurchaseOrderListItem>
        module="purchase-orders"
        breadcrumb={['Procurement', 'Purchasing', 'Purchase Orders']}
        columns={COLUMNS}
        facets={[
          { key: 'status', label: 'Status' },
          { key: 'stage', label: 'Stage' },
          { key: 'supplier', label: 'Supplier' },
        ]}
        /*
          FILED BY STAGE. A reader opens this register to separate the drafts from what is
          waiting on an approver and what has been approved; `status` stays in the rail with Show
          deleted, where it is on every list in the product.
        */
        folderNavFacet="stage"
        auditEntityTypes={['PurchaseOrder', 'PurchaseOrderItem']}
        rowKey={(row) => row.id}
        fetch={fetchRows}
        reloadKey={reloadKey}
        searchPlaceholder="Search by PO number, supplier, PR number or item"
        emptyMessage="No purchase order yet. Place one against an approved request."
        prerequisites={[
          {
            /*
              PLURAL, because the SHELL uses this one label in three sentences that do not all
              want the same grammar: the title reads "Set up … first", the body reads "Nothing has
              been added to … yet", and the button reads "Go to …". An article form gives a title
              that reads and a body that does not — measured on the rendered banner, which is the
              only place the three are visible together. `categories` and `item types` one suite
              along already take the plural.
            */
            label: 'suppliers',
            met: hasSupplier,
            to: '/procurement/purchasing/settings/suppliers',
            permission: 'suppliers.read',
          },
          {
            /*
              PLURAL again, for the same three sentences. It points at the REQUEST register
              rather than at a settings page, because that is where a reader goes to raise one —
              the prerequisite's `to` is the way to satisfy it, not necessarily a catalog.
            */
            label: 'approved purchase requests',
            met: hasOrderableRequest,
            to: '/procurement/purchasing/purchase-requests',
            permission: 'purchase-requests.read',
          },
        ]}
        onCreate={() => setCreating(true)}
        createPermission="purchase-orders.create"
        // The anchor opens the profile. The READ key — opening a record is not changing it.
        onRowOpen={(row) => navigate(`/procurement/purchasing/purchase-orders/${row.id}`)}
        openPermission="purchase-orders.read"
        rowActions={rowActions}
        /*
          THE RING — product call, 05 Oct 2026, after the reader opened this register and said
          *i dont see the ring indicator*. The request register one along had carried one since
          the 1st; this one was recorded as "deliberately not built" because its UNIT was an
          open product call, and the answer is QUANTITY: `receivedQuantity / totalQuantity`
          over the live lines.

          SO THE TWO REGISTERS COUNT DIFFERENT THINGS, deliberately. A request's ring counts
          LINES, because a requester asks how many of the things they asked for have arrived; an
          order's counts QUANTITY, because nine of ten laptops on one line is 0% by lines and
          90% by quantity, and the second is what a buyer chasing a supplier wants to know.

          `fulfilmentOf` returns null until the order has been SENT, so the drafts and the
          approvals beside it draw nothing rather than an empty circle reading 0% — they are
          outside the question rather than at the start of it. The table states the same
          percentage in its `Fulfilled` column, off this same derivation.

          THE LABEL NAMES NO UNIT, which is a departure from `<ProgressPie>`'s own contract —
          its `label` is documented as "the only place the unit is named", and the sum behind
          this one HAS no unit: it adds boxes to laptops. So the screen-reader sentence reads
          *12 of 40 received* rather than inventing a noun the figure cannot support.
        */
        rowProgress={(row) => {
          const progress = fulfilmentOf(row);

          return progress
            ? { value: progress.done, total: progress.total, label: 'received' }
            : null;
        }}
        onDelete={(row) => setConfirming({ kind: 'delete', row })}
        deletePermission="purchase-orders.delete"
        deleteLabel={() => 'Delete'}
        deleteDisabledReason={(row) => (row.isActive ? null : 'This order is already deleted.')}
        otherInfo={(row) => row}
      />

      {creating ? (
        <PurchaseOrderModal
          open
          editing={null}
          onClose={() => setCreating(false)}
          onSaved={() => {
            setCreating(false);
            refresh();
          }}
        />
      ) : null}

      {cancelling ? (
        <CancelPurchaseOrderModal
          open
          order={cancelling}
          isSubmitted={cancelling.status === PO_STATUS.submitted}
          onClose={() => setCancelling(null)}
          onCancelled={() => {
            setCancelling(null);
            refresh();
          }}
        />
      ) : null}

      {confirming ? (
        <ConfirmDialog
          open
          onClose={() => {
            setConfirming(null);
            setError(null);
          }}
          onConfirm={() => void runConfirmed()}
          title={TITLES[confirming.kind]}
          danger={confirming.kind === 'delete'}
          // Both safeguards on the destructive half only. Submitting and restoring need none.
          requireConfirm={confirming.kind === 'delete'}
          requireCredential={confirming.kind === 'delete'}
          loading={running}
          id={`purchase-order-${confirming.kind}-dialog`}
          body={
            <div className="flex flex-col gap-2">
              {confirming.kind === 'submit' ? (
                <>
                  <p>
                    {confirming.row.orderNumber} is routed to whoever signs off on purchase
                    orders. It stops being editable at that point.
                  </p>
                  <p>
                    If nobody has been configured as an approver, it goes to the administrators
                    and any one of them can approve it.
                  </p>
                </>
              ) : null}

              {/*
                DELETING IS NOT CANCELLING, and the two are the most confusable states here. The
                dialog says so outright and points a reader who reached for the wrong one at the
                other — the disciplinary record's own dialog takes this exact shape.
              */}
              {confirming.kind === 'delete' ? (
                <>
                  <p>
                    {confirming.row.orderNumber} is hidden from the register. Any approval still
                    pending on it is closed, and every quantity it committed goes back to the
                    purchase requests it drew on.
                  </p>
                  <p>
                    <strong>This is not the same as cancelling it.</strong> Deleting is for an
                    order keyed against the wrong record, or twice. If the organization decided
                    not to place it, use <strong>Cancel order</strong> instead — that is an
                    outcome the register keeps, with a reason.
                  </p>
                  <p>Nothing is erased — a deleted order can be restored from this list.</p>
                </>
              ) : null}

              {confirming.kind === 'restore' ? (
                <>
                  <p>{confirming.row.orderNumber} returns to the register with its lines intact.</p>
                  <p>
                    It takes its quantities back from the source requests, so this can be refused
                    if those lines have been ordered against while it was hidden.
                  </p>
                </>
              ) : null}

              {error ? (
                <p
                  role="alert"
                  className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700"
                >
                  {error.message}
                </p>
              ) : null}
            </div>
          }
        />
      ) : null}
    </>
  );
}

const TITLES = {
  delete: 'Delete purchase order',
  restore: 'Restore purchase order',
  submit: 'Submit for approval',
} as const;

const COLUMNS: ColumnDef<PurchaseOrderListItem>[] = [
  {
    /*
      THE PO NUMBER is the anchor, and it CARRIES THE PRODUCTS — the treatment the purchase
      request register beside it already takes, on the same product call: a register of
      `PO-26-00006` rows says which order without saying which order is which.

      ONLY THE NUMBER IS MONO — product call, 01 Oct 2026, in the reader's own words: *in the PO
      card list, do not use font mono on the title, except for the PO number*. The register
      carried `mono` over the WHOLE joined string, so a run of product names read in the machine
      face — which is false of them and makes the signal mean less everywhere it is true.

      `code` is what splits the cell, and it is the same prop the purchase request register
      beside it already takes. It also owns the ` — `, so the two registers cannot punctuate one
      pattern differently and the empty-items case is the component's rather than a ternary here.

      `clamp` is the max width, because the joined value has spaces and `<TextCell>` wraps a
      multi-word value by default — which would make this row taller than the forty around it
      and cost the table the horizontal line it is scanned along.
    */
    key: 'orderNumber',
    header: 'PO number',
    width: 400,
    sortable: true,
    /*
      THE SOURCE REQUEST NUMBER FOLLOWS THE ORDER NUMBER — product call, 05 Oct 2026: *concat
      the PR no, after the PO*. `PO-26-00009 — PR-26-00015 — Claude Anthropic`.

      BOTH GO IN `code`, which is what keeps them in the MONO face: a purchase order number and
      a purchase request number are both codes, and the prose half is the products. Splitting
      them across `code` and `value` would put the PR number in the body face beside the
      products, which is the one thing the two-face anchor exists to prevent.

      A COMPOSITE ORDER LISTS EVERY ONE of them — `sourceRequestNumbers` is already the joined
      value — so an order drawing on two requests reads both, which is what the count badge
      beside the old column was standing in for.

      WIDENED to 400 from 320, because the cell now holds three values rather than two. It still
      `clamp`s: the joined value has spaces, and `<TextCell>` wraps a multi-word value by
      default, which would make this row taller than the forty around it.
    */
    render: (row) => (
      <TextCell
        code={
          row.sourceRequestNumbers
            ? `${row.orderNumber} — ${row.sourceRequestNumbers}`
            : row.orderNumber
        }
        value={row.items}
        clamp
      />
    ),
  },
  {
    key: 'supplierName',
    header: 'Supplier',
    width: 220,
    sortable: true,
    summary: true,
    render: (row) => <TextCell value={row.supplierName} clamp />,
  },
  {
    key: 'lineCount',
    header: 'Lines',
    width: 80,
    align: 'right',
    /*
      NOT a summary value: the list view concatenates its summary values with no labels, so a
      count arrives as a bare `· 2` that a reader cannot interpret without this header.
    */
    render: (row) => <TextCell value={String(row.lineCount)} mono numeric />,
  },
  {
    key: 'totalQuantity',
    header: 'Qty',
    width: 90,
    align: 'right',
    sortable: true,
    render: (row) => <TextCell value={String(row.totalQuantity)} mono numeric />,
  },
  {
    key: 'fulfilled',
    header: 'Fulfilled',
    width: 100,
    align: 'right',
    /*
      THE TABLE'S HALF OF THE FULFILMENT FORMAT. The list row draws this as a ring before its
      kebab; a table row is one line tall and its trailing edge is a pinned 56px gutter, so the
      ring cannot go there — and without this column the list would state a fact the table
      cannot, which is what reference §3 refuses.

      ONE derivation behind both: `fulfilmentOf` decides whether the question applies and
      `progressPercent` rounds it, so the column and the ring cannot answer one order
      differently.

      AN EM DASH where delivery is not yet a question — a draft, one awaiting a decision, one
      rejected, one approved and still on the buyer's desk. That is not 0%: zero would claim
      nothing has arrived against an order nothing CAN be delivered against, which is the
      null-versus-zero distinction the Total column one over already draws the other way.

      AND `numeric`, so the 0% DIMS — a percentage is a quantity, and a dispatched order with
      nothing received is exactly the empty the eye should be able to skip past.

      NOT sortable: the server's allow-list does not name it, and a header that silently fell
      back to the default sort reads as a broken control.
    */
    render: (row) => {
      const progress = fulfilmentOf(row);

      return (
        <TextCell
          value={progress ? `${progressPercent(progress.done, progress.total)}%` : null}
          mono
          numeric
        />
      );
    },
  },
  {
    key: 'totalAmount',
    header: 'Total',
    width: 150,
    align: 'right',
    sortable: true,
    /*
      NEVER AN EM DASH, unlike the request's estimate one register along: an order line carries a
      required unit cost, so every order has a total and a zero is a figure somebody agreed. It
      is `mono numeric`, so that zero dims.
    */
    render: (row) => <TextCell value={formatMoney(row.totalAmount)} mono numeric />,
  },
  {
    key: 'expectedDeliveryDate',
    header: 'Expected',
    width: 140,
    /*
      `formatDateOnly` through a `<TextCell mono>`, never `<DateCell>`. A delivery is promised for
      a DAY rather than an instant, and putting a calendar date through the instant path tells a
      reader west of UTC the day before the one on the form.
    */
    render: (row) => (
      <TextCell
        value={row.expectedDeliveryDate ? formatDateOnly(row.expectedDeliveryDate) : null}
        mono
      />
    ),
  },
  {
    /*
      THE LIFECYCLE AS PLAIN TEXT, and the row's own live-or-deleted state as the pill below.
      TWO state columns deliberately — a deleted order and a cancelled one are different facts,
      and a reader must not have to guess which the column is reporting.
    */
    key: 'stage',
    header: 'Stage',
    width: 130,
    sortable: true,
    // Moves it to sit immediately before the status pill, and beside the badge on a list row.
    stage: true,
    render: (row) => <StatusText tone="inactive">{row.statusLabel}</StatusText>,
  },
  {
    key: 'status',
    header: 'Status',
    width: 120,
    status: true,
    render: (row) =>
      row.isActive ? (
        <StatusText tone="active">Active</StatusText>
      ) : (
        <StatusText tone="inactive">Deleted</StatusText>
      ),
  },
];
